import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { CounterOrderModel } from "./CounterOrderModel";

interface ICounterOrderComponentProps {
    models: CounterOrderModel[];
    counter: number;

    readonly?: boolean;

    onModelUpdate: (models: CounterOrderModel, counter: number) => void;
}

class CounterOrderComponent extends BaseReactComponent<ICounterOrderComponentProps> {
    private readonly maxColumn: number = 5;

    public render(): JSX.Element {
        if (!this.props.models?.length)
            return null;

        return (
            <div className="counter-order">
                {this.splitIntoVerticalSplices(this.props.models).map((columns, idx) => this.renderRow(columns, idx))}
            </div>
        );
    }

    private renderRow(fields: CounterOrderModel[], index: number): JSX.Element {
        let emptyFields = [];
        for (let i = 0; i < this.maxColumn - fields.length; i++)
            emptyFields.push(this.renderField(null, i));

        return (
            <div key={index} className="flex-container">
                {fields.map((model, idx) => this.renderField(model, idx))}
                {emptyFields}
            </div>
        );
    }

    private renderField(model: CounterOrderModel, index: number): JSX.Element {
        if (!model)
            return <div key={index} className="row column margin-y-0" />;

        return (
            <div key={index} className="row column margin-y-0">
                <div className="field">
                    {this.renderTexboxField(model)}
                </div>
                <div className="label">
                    <div>
                        {this.renderLabelField(model)}
                        {this.renderCheckboxFields(model)}
                    </div>
                </div>
            </div>
        );
    }

    private renderTexboxField(model: CounterOrderModel): JSX.Element {
        if (this.props.readonly)
            return <span className="field-value">{model.Value}</span>;

        return (
            <TextboxControl
                className="field-value form-control input"
                value={model.Value ?? ""}
                maxLength={3}
                onChange={value => model.setValue(value)}
                onBlur={() => this.props.onModelUpdate?.(model, this.props.counter)}
            />
        );
    }

    private renderLabelField(model: CounterOrderModel): JSX.Element {
        if (this.props.readonly)
            return <span className="label-value">{cacheStore.GetString(model.Label)}</span>;

        return <a className="label-value" onClick={() => this.handleOnLabelClick(model)}>{cacheStore.GetString(model.Label)}</a>;
    }

    private renderCheckboxFields(model: CounterOrderModel): JSX.Element {
        if (!model.hasExtra())
            return null;

        return (
            <span className="extra-field">
                {model.Extra.map((p, i) => (
                    <CheckboxControl
                        key={i}
                        checked={p.IsCheck}
                        label={cacheStore.GetString(p.Label)}
                        disabled={this.props.readonly}
                        onChange={checked => {
                            model.setExtraValue(p.Id, checked);
                            this.props.onModelUpdate?.(model, this.props.counter);
                        }}
                    />
                ))}
            </span>
        );
    }

    private handleOnLabelClick(model: CounterOrderModel): void {
        const nextNumber = (this.props.counter ?? 0) + 1;
        model.setNumberValue(nextNumber);
        this.props.onModelUpdate?.(model, nextNumber);
    }

    private splitIntoVerticalSplices(models: CounterOrderModel[]): CounterOrderModel[][] {
        let rows = [], currentRow = [];
        let rowIndex = 0, modelIndex = 0;

        let maxRow = Math.ceil(models.length / this.maxColumn);

        while (modelIndex < models.length && rowIndex < maxRow) {
            currentRow.push(models[modelIndex]);

            modelIndex += maxRow;
            if (modelIndex >= models.length) {
                rows.push(currentRow);

                rowIndex++;
                modelIndex = rowIndex;
                currentRow = [];
            }
        }

        return rows;
    }
}

export { CounterOrderComponent };
